import React from 'react';
import { cn } from '../../../lib/utils';
import { StateChip } from '../../shared/StateChip';

export type HonestTier = 'no-tool' | 'no-input' | 'non-gating';
export type HonestScale = 'chip' | 'row' | 'panel';

export interface HonestStateProps {
  tier: HonestTier;
  scale: HonestScale;
  label: string;
  body?: string;
  action?: { text: string; onClick?: () => void };
  className?: string;
}

/* Markers set color via Tailwind arbitrary classes (real CSS), NOT via
 * stroke="var(--x)" / fill="var(--x)" presentation attributes — CSS custom
 * properties do not resolve in SVG presentation attributes (fails in Safari),
 * so the attribute form would render the marker colorless. */

const H1Marker = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="shrink-0">
    <rect x="0.5" y="0.5" width="9" height="9" strokeWidth="1" className="stroke-[var(--color-border-sub)]" />
    <line x1="2" y1="5" x2="8" y2="5" strokeWidth="1" className="stroke-[var(--color-text-ghost)]" />
  </svg>
);

const H2Marker = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="shrink-0">
    <rect x="0.5" y="0.5" width="9" height="9" strokeWidth="1" className="stroke-[var(--color-needs-human-tint-bd)]" />
    <text
      x="5"
      y="7.5"
      textAnchor="middle"
      fontSize="6"
      fontFamily="Outfit, sans-serif"
      fontWeight="600"
      className="fill-[var(--color-needs-human-text)]"
    >
      !
    </text>
  </svg>
);

const H3Check = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0 stroke-[var(--color-verified)]"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function HonestState({ tier, scale, label, body, action, className }: HonestStateProps) {
  if (scale === 'chip') {
    if (tier === 'no-tool') {
      return <StateChip state="dormant" text={label} />;
    }
    if (tier === 'no-input') {
      return <StateChip state="needs-human" text={label} />;
    }
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <StateChip state="verified" text={label} />
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] border border-[var(--color-border-sub)] text-[var(--color-text-dim)] h-[16px] flex items-center px-1.5">
          NON-GATING
        </span>
      </div>
    );
  }

  if (scale === 'row') {
    const isH1 = tier === 'no-tool';
    const isH2 = tier === 'no-input';
    const isH3 = tier === 'non-gating';

    return (
      <div className={cn('flex items-center gap-2', className)}>
        {isH1 && <H1Marker />}
        {isH2 && <H2Marker />}
        {isH3 && <H3Check />}

        {isH1 && (
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-dim)]">
            {label}
          </span>
        )}

        {isH2 && (
          <>
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)]">
              {label}
            </span>
            {action && (
              <button
                onClick={action.onClick}
                className="font-sans text-[11px] text-[var(--color-needs-human-text)] hover:text-[var(--color-brand-hover)] transition-colors duration-150"
              >
                {action.text}
              </button>
            )}
          </>
        )}

        {isH3 && (
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] border border-[var(--color-border-sub)] text-[var(--color-text-dim)] h-[16px] flex items-center px-1.5">
            NON-GATING
          </span>
        )}
      </div>
    );
  }

  if (scale === 'panel') {
    const isH1 = tier === 'no-tool';
    const isH2 = tier === 'no-input';

    const borderClass = isH1
      ? 'border-l border-dashed border-[var(--color-border)]'
      : isH2
        ? 'border-l-2 border-[var(--color-brand)]'
        : 'border-l-2 border-[var(--color-verified)]';

    return (
      <div className={cn('pl-3', borderClass, className)}>
        <div className="flex items-center gap-2 mb-1">
          {isH1 && (
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-dim)]">
              {label}
            </span>
          )}
          {isH2 && (
            <>
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)]">
                {label}
              </span>
              {action && (
                <button
                  onClick={action.onClick}
                  className="font-sans text-[11px] text-[var(--color-needs-human-text)] hover:text-[var(--color-brand-hover)] transition-colors duration-150"
                >
                  {action.text}
                </button>
              )}
            </>
          )}
          {!isH1 && !isH2 && (
            <>
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-verified)]">
                COMPUTED ✓
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-text-dim)]">
                NON-GATING
              </span>
            </>
          )}
        </div>
        {body && (
          <p className="font-sans text-[12px] text-[var(--color-text-muted)] leading-relaxed">
            {body}
          </p>
        )}
      </div>
    );
  }

  return null;
}

export default HonestState;
